import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './RegisterPage.css'

const LOGO_URL = 'https://res.cloudinary.com/dk50cmtps/image/upload/v1786338929/unnamed_efi40j.png'
const LOGO_SIDE_IMG_URL = 'https://res.cloudinary.com/dk50cmtps/image/upload/v1786336638/592f1aee-a328-4135-bd3f-8a1e20477346_smm1fr.png'

const API_BASE_URL = 'https://upskld.co/api'

const registerSchema = Yup.object({
  fullName: Yup.string()
    .trim()
    .min(3, 'Full name must be at least 3 characters')
    .required('Full name is required'),
  phoneNumber: Yup.string()
    .trim()
    .matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit phone number')
    .notRequired(),
  email: Yup.string()
    .trim()
    .email('Enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  agreed: Yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
})

const Register = () => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const formik = useFormik({
    initialValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      password: '',
      agreed: false,
    },
    validationSchema: registerSchema,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true)
      try {
        const response = await axios.post(`${API_BASE_URL}/register`, values)

        if (response.data.success) {
          // No local progress reset needed here — a brand-new account has
          // no rows yet in the "skills" collection at all, and CoursePage /
          // ProfilePage both already treat "no record" as 0% / locked.
          toast.success(response.data.message || 'Registration successful!')
          resetForm()
          setTimeout(() => {
            navigate('/login')
          }, 1500)
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          'Something went wrong. Please try again.'
        toast.error(errorMessage)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const goToLogin = () => {
    navigate('/login')
  }


  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  return (
    <div className="regi_pg_container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="regi_pg_left">
        <div className="regi_pg_logo_row">
          <img
            src={LOGO_SIDE_IMG_URL}
            alt="Rubicr HR"
            className="regi_pg_logo_side_img"
            width="48"
            height="48"
          />
          <img
            src={LOGO_URL}
            alt="Rubicr"
            className="regi_pg_logo_img"
            width="56"
            height="56"
          />
        </div>
      </div>

      <div className="regi_pg_right">
        <div className="regi_pg_form_wrap">
          <h2 className="regi_pg_title">Create an Account</h2>
          <p className="regi_pg_subtitle">Create your Rubicr account today and get started</p>

          <form className="regi_pg_form" onSubmit={formik.handleSubmit} noValidate>
            <div className="regi_pg_field">
              <label className="regi_pg_label" htmlFor="fullName">
                Full Name <span className="regi_pg_required">*</span>
              </label>
              <input
                id="fullName"
                name="fullName"
                type="text"
                className="regi_pg_input"
                placeholder="Enter your Full Name"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.fullName && formik.errors.fullName && (
                <span className="regi_pg_error_text">{formik.errors.fullName}</span>
              )}
            </div>

            <div className="regi_pg_field">
              <label className="regi_pg_label" htmlFor="phoneNumber">
                Phone Number
              </label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                className="regi_pg_input"
                placeholder="Enter your phone number"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <span className="regi_pg_error_text">{formik.errors.phoneNumber}</span>
              )}
            </div>

            <div className="regi_pg_field">
              <label className="regi_pg_label" htmlFor="email">
                Email Address <span className="regi_pg_required">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                className="regi_pg_input"
                placeholder="Enter your email address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.email && formik.errors.email && (
                <span className="regi_pg_error_text">{formik.errors.email}</span>
              )}
            </div>

            <div className="regi_pg_field">
              <label className="regi_pg_label" htmlFor="password">
                Password <span className="regi_pg_required">*</span>
              </label>
              <div className="regi_pg_password_wrap">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="regi_pg_input regi_pg_input_password"
                  placeholder="Create a password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <button
                  type="button"
                  className="regi_pg_eye_btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-10-8-10-8a18.6 18.6 0 0 1 4.22-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s3-8 11-8 11 8 11 8-3 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {formik.touched.password && formik.errors.password && (
                <span className="regi_pg_error_text">{formik.errors.password}</span>
              )}
            </div>

            <div className="regi_pg_checkbox_row">
              <input
                id="agreed"
                name="agreed"
                type="checkbox"
                className="regi_pg_checkbox"
                checked={formik.values.agreed}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <label htmlFor="agreed" className="regi_pg_checkbox_label">
                I agree to your terms and conditions
              </label>
            </div>
            {formik.touched.agreed && formik.errors.agreed && (
              <span className="regi_pg_error_text">{formik.errors.agreed}</span>
            )}

            <button type="submit" className="regi_pg_submit_btn" disabled={isSubmitting}>
              {isSubmitting ? 'Please wait...' : (
                <>
                  Save &amp; Proceed <span className="regi_pg_arrow">&rarr;</span>
                </>
              )}
            </button>

            <p className="regi_pg_login_text">
              Already have an account?{' '}
              <span className="regi_pg_login_link" onClick={goToLogin}>
                Login Here
              </span>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Register